import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Feedback, Loading } from '@cubeyou-team/hx-react-components';

import uniqid from 'uniqid';
import { useSelector } from 'react-redux';
import {
  StyledQueryBuilderWithPreviewHost,
  StyledQueryBuilderWrapper,
  StyledQueryBuilderMain,
  StyledQueryBuilderScroll,
} from './StyledQueryBuilder';

import QueryTargets from './components/QueryTargets';
import FixedQueryPreview from './components/FixedQueryPreview';
import { StanleyQueryPanel } from '../../modules/query-builder/components/stanley-query/components/stanley-query-panel';
import { PANEL_VIEWS } from '../../modules/query-builder/components/stanley-query/components/stanley-query-panel/providers/StanleyQueryPanelContext';
import { isStanleyQuerySupportedDataSource } from '../../modules/query-builder/components/stanley-query/utils/isStanleyQuerySupportedDataSource';

/** Full-width row in Create Audience modal (query column + gtTaxonomyExplorer). */
const CREATE_AUDIENCE_MODAL_ROW_ID = 'gtCreateAudienceModalRow';

const QueryBuilder = ({
  onQueryErrorClose,
  onItemDragToggle,
  onItemDropped,
  conversation,
  initializeQueryBuilder,
  clearQueryRequest,
  isQueryCreationLoading,
  entityDragging,
  onRequireMissingItemRequest,
}) => {
  const queryBuilder = useSelector((state) => state.queryBuilder);
  const {
    isNewQuery,
    target_selected,
    isCompareQuery,
    dataSource,
    queryBuilderType,
  } = queryBuilder;
  const [isQueryPreviewVisible, setIsQueryPreviewVisible] = useState(false);
  const [isStanleyQueryPanelVisible, setIsStanleyQueryPanelVisible] =
    useState(false);
  const [stanleyPanelInitialView, setStanleyPanelInitialView] = useState(
    PANEL_VIEWS.FORM
  );
  const [stanleyPanelView, setStanleyPanelView] = useState(PANEL_VIEWS.FORM);
  const [openStanleyHistoryRequestId, setOpenStanleyHistoryRequestId] =
    useState(0);

  useEffect(() => {
    if (isNewQuery || isCompareQuery) {
      initializeQueryBuilder(uniqid());
    }
  }, [initializeQueryBuilder, isNewQuery, isCompareQuery]);

  useEffect(() => setIsQueryPreviewVisible(false), [target_selected]);

  useEffect(() => {
    if (
      !isStanleyQuerySupportedDataSource(dataSource) ||
      queryBuilderType === 'conversation'
    ) {
      setIsStanleyQueryPanelVisible(false);
      setStanleyPanelInitialView(PANEL_VIEWS.FORM);
      setStanleyPanelView(PANEL_VIEWS.FORM);
    }
  }, [dataSource, queryBuilderType]);

  const queryPreview = useMemo(() => {
    const preview = {
      ...queryBuilder.targets[target_selected],
    };
    return preview;
  }, [queryBuilder, target_selected]);

  const handleToggleQueryPreview = useCallback(() => {
    const newValue = !isQueryPreviewVisible;
    setIsQueryPreviewVisible(newValue);
  }, [isQueryPreviewVisible]);

  const handleCloseStanleyQueryPanel = useCallback(() => {
    setIsStanleyQueryPanelVisible(false);
    setStanleyPanelInitialView(PANEL_VIEWS.FORM);
    setStanleyPanelView(PANEL_VIEWS.FORM);
  }, []);

  const handleToggleStanleyQueryPanel = useCallback(() => {
    setIsStanleyQueryPanelVisible((isVisible) => {
      if (isVisible) {
        setStanleyPanelInitialView(PANEL_VIEWS.FORM);
        setStanleyPanelView(PANEL_VIEWS.FORM);
        return false;
      }

      setStanleyPanelInitialView(PANEL_VIEWS.FORM);
      return true;
    });
  }, []);

  const handleOpenStanleyHistory = useCallback(() => {
    if (isStanleyQueryPanelVisible) {
      setOpenStanleyHistoryRequestId((id) => id + 1);
      return;
    }

    setStanleyPanelInitialView(PANEL_VIEWS.HISTORY);
    setStanleyPanelView(PANEL_VIEWS.HISTORY);
    setIsStanleyQueryPanelVisible(true);
  }, [isStanleyQueryPanelVisible]);

  const handleStanleyPanelViewChange = useCallback((view) => {
    setStanleyPanelView(view);
  }, []);

  const handleFeedbackClose = useCallback(
    () => onQueryErrorClose(),
    [onQueryErrorClose]
  );

  const isLoading =
    queryBuilder.isLoading || conversation.isLoading || isQueryCreationLoading;

  const previewPortalRoot =
    typeof document !== 'undefined'
      ? document.getElementById(CREATE_AUDIENCE_MODAL_ROW_ID)
      : null;

  const queryBuilderModal = (
    <StyledQueryBuilderWrapper
      data-test-id="query-builder"
      id="gtCreateAudienceModal"
    >
      {queryBuilder.queryBuilderType !== 'conversation' &&
        queryBuilder.error && (
          <Box pY="small" pX="small">
            <Feedback
              data-test-id="query-builder-error-feedback"
              status="error"
              message={queryBuilder.message}
              closable
              onFeedbackClose={handleFeedbackClose}
            />
          </Box>
        )}
      <Loading isLoading={isLoading}>
        <StyledQueryBuilderScroll>
          <StyledQueryBuilderMain>
            <QueryTargets
              entityDragging={entityDragging}
              onRequireMissingItemRequest={onRequireMissingItemRequest}
              onItemDragToggle={onItemDragToggle}
              onItemDropped={onItemDropped}
              onQueryPreviewRequest={handleToggleQueryPreview}
              onClearQueryRequest={clearQueryRequest}
              isQueryPreviewVisible={isQueryPreviewVisible}
              isStanleyQueryPanelVisible={isStanleyQueryPanelVisible}
              isStanleyHistoryView={stanleyPanelView === PANEL_VIEWS.HISTORY}
              onStanleyQueryPanelToggle={handleToggleStanleyQueryPanel}
              onOpenStanleyHistory={handleOpenStanleyHistory}
              isQueryLoading={isLoading}
            />
          </StyledQueryBuilderMain>
        </StyledQueryBuilderScroll>
      </Loading>
    </StyledQueryBuilderWrapper>
  );

  const fixedQueryPreviewEl = isQueryPreviewVisible && (
    <FixedQueryPreview
      queryPreview={queryPreview}
      onClose={handleToggleQueryPreview}
    />
  );

  const stanleyQueryPanelEl = isStanleyQueryPanelVisible && (
    <StanleyQueryPanel
      onClose={handleCloseStanleyQueryPanel}
      initialView={stanleyPanelInitialView}
      openHistoryRequestId={openStanleyHistoryRequestId}
      onViewChange={handleStanleyPanelViewChange}
    />
  );

  const hasOverlayPanels = isQueryPreviewVisible || isStanleyQueryPanelVisible;

  if (hasOverlayPanels && previewPortalRoot) {
    return (
      <>
        {queryBuilderModal}
        {isQueryPreviewVisible &&
          createPortal(fixedQueryPreviewEl, previewPortalRoot)}
        {isStanleyQueryPanelVisible &&
          createPortal(stanleyQueryPanelEl, previewPortalRoot)}
      </>
    );
  }

  if (hasOverlayPanels) {
    return (
      <StyledQueryBuilderWithPreviewHost>
        {queryBuilderModal}
        {fixedQueryPreviewEl}
        {stanleyQueryPanelEl}
      </StyledQueryBuilderWithPreviewHost>
    );
  }

  return queryBuilderModal;
};

export default QueryBuilder;
